import moment from "moment-timezone";
import isEmpty from "lodash/isEmpty";
import config from "@/config";

export const PUZZLE_SCORE_EMOJIS = {
    correct: "🟪",
    incorrect: "⬜",
    close: "🟧",
};

const INCORRECT_THRESHOLD = 2;
const DEFAULT_VERSION_URL = "puzzle";

export interface BuildPuzzleShareDetailsParams {
    guesses: Array<string | number | null | undefined>;
    actualPrice: number;
    playDate?: string;
    shortLink: string;
    versionUrl?: string;
    /**
     * Optional human-readable label for the property, e.g. "1969 El Dorado Ave | Berkeley".
     * When provided, this will be used in the share title instead of the play date.
     */
    propertyLabel?: string;
}

export interface PuzzleShareDetails {
    shareTextTitle: string;
    shareTextBody: string;
    shareUrl: string;
}

/**
 * Builds an address label for a property to use in share text.
 * Example output: "1969 El Dorado Ave | Berkeley".
 */
export const buildPropertyShareLabel = (property: any): string => {
    if (!property) return "";

    const street =
        property.streetAddress ||
        property.street_address ||
        "";
    const city = property.city || "";

    if (street && city) return `${street} | ${city}`;
    return street || city || "";
};

export const determineTileMode = (actualDigit: number, tileValue: number): keyof typeof PUZZLE_SCORE_EMOJIS => {
    if (tileValue === actualDigit) return "correct";

    const inThreshold =
        tileValue >= actualDigit - INCORRECT_THRESHOLD && tileValue <= actualDigit + INCORRECT_THRESHOLD;

    return inThreshold ? "close" : "incorrect";
};

const formatGuessRow = (guessValue: string, actualDigits: number[]): string => {
    const digits = guessValue.padStart(8, "0").split("");

    return digits
        .map((digitValue, index) => {
            const tileMode = determineTileMode(actualDigits[index] ?? 0, Number(digitValue));
            const separator = index === 1 || index === 4 ? "," : "";
            return `${PUZZLE_SCORE_EMOJIS[tileMode]}${separator}`;
        })
        .join("");
};

export const buildPuzzleShareDetails = ({
    guesses,
    actualPrice,
    playDate,
    shortLink,
    versionUrl = DEFAULT_VERSION_URL,
    propertyLabel,
}: BuildPuzzleShareDetailsParams): PuzzleShareDetails => {
    const actualDigits = actualPrice.toString().padStart(8, "0").split("").map(Number);
    const validGuesses = guesses.filter(
        (guess): guess is string | number => !isEmpty(guess) || guess === 0,
    );

    const formattedRows = validGuesses.map((guess) => {
        const guessString = typeof guess === "number" ? guess.toString() : guess;
        return formatGuessRow(guessString, actualDigits);
    });

    // Property label (address) if provided; otherwise, fall back to the play date.
    let titleLabel: string;
    if (propertyLabel && propertyLabel.trim().length > 0) {
        titleLabel = propertyLabel.trim();
    } else if (playDate) {
        const playMoment = moment.tz(playDate, "America/Chicago");
        titleLabel = playMoment.isValid() ? playMoment.format("MMM D, YYYY") : moment().format("MMM D, YYYY");
    } else {
        titleLabel = moment().format("MMM D, YYYY");
    }

    return {
        // Title is kept without trailing newline so callers can control formatting
        // Example: "PriceMe: 1969 El Dorado Ave | Berkeley"
        shareTextTitle: `PriceMe: ${titleLabel}`,
        shareTextBody: formattedRows.join("\n"),
        shareUrl: `${config.BASE_URL}/${versionUrl}/${shortLink}?utm_medium=share`,
    };
};

export const hasSolvedPuzzle = (guesses: Array<string | number | null | undefined>, actualPrice: number): boolean => {
    const actual = actualPrice.toString();
    return guesses.some((guess) => {
        if (isEmpty(guess) && guess !== 0) return false;
        const guessString = typeof guess === "number" ? guess.toString() : guess;
        return Number(guessString) === Number(actual);
    });
};
